import _, { memoize } from 'lodash';
import memoizee from 'memoizee';
import { createMatchObjectFilter } from 'nwa-query-builder';
import registerPromiseWorker from 'promise-worker/register';
import { workerFactory } from '../../generic';
import { isParticipantActive, isParticipantContact } from '../filters';
import { filterParticipantsByCategories } from '../filters/filterParticipantsByCategories';
import { isParticipantConnection } from '../filters/isParticipantConnection';
import { isParticipantFavorite } from '../filters/isParticipantFavorite';
import { isParticipantMutualFavorite } from '../filters/isParticipantMutualFavorite';
import { matchParticipantText } from '../filters/matchParticipantText';


/**
 * Creates a factory function that returns a memoized function for filtering participants in a web worker or the main thread.
 *
 * @param {string} workerURL - The URL of the web worker script.
 * @return {function} - A memoized function that filters participants in a web worker or the main thread.
 */
export function participantsFilterWorkerFactory(workerURL) {
    return workerFactory(workerURL, filterParticipants);
}

export const registerParticipantsFilterWorker = memoize(() => {
    registerPromiseWorker(filterParticipants);
});

/**
 * Filters participants based on the provided filter parameters.
 *
 * @param {Object} options - The options object.
 * @param {Array} options.participants - The array of participant objects.
 * @param {Object} options.filterParams - The filter parameters object.
 * @param {Array} options.filterParams.connections - The array of connection objects.
 * @param {boolean} options.filterParams.active - Indicates if only active participants should be included.
 * @param {boolean} options.filterParams.contacts - Indicates if only contacts should be included.
 * @param {string} options.filterParams.text - The text to filter participants by.
 * @param {Object} options.filterParams.categories - The categories object used for filtering.
 * @param {boolean} options.filterParams.favorites - Indicates if only favorites should be included.
 * @param {boolean} options.filterParams.mutualFavorites - Indicates if only mutual favorites should be included.
 * @param {Object} options.filterParams.query - The query object used for filtering.
 * @param {Array} options.categories - The array of category objects.
 * @param {Array} options.connections - The array of connection objects.
 * @return {Object} - An object containing the filtered participants, categories, and filter parameters.
 */
function filterParticipants({participants, filterParams, categories, connections}) {
    let filtered = participants

    // console.log('DEBUG FILTER PARTICIPANTS', participants, filterParams, categories, connections);

    // Apply connections filter
    if (filterParams.connections) {
        const indexedConnections = indexConnections(connections);
        filtered = filtered.filter(p => p.user_id && isParticipantConnection(p, indexedConnections[p.user_id], filterParams.connections));
    }

    // Apply active filter
    if (filterParams.active) {
        filtered = filtered.filter(isParticipantActive);
    }

    // Apply contacts filter
    if (filterParams.contacts) {
        filtered = filtered.filter(isParticipantContact);
    }

    // First apply text filter
    if (filterParams.text) {
        _(filterParams.text.split(/\s/))
            .compact()
            .each(word => {
                let exp = new RegExp(_.escapeRegExp(word), 'i');
                filtered = filtered.filter(p => matchParticipantText(p, exp));
            });
    }

    // Apply categories filter
    filtered = filterParticipantsByCategories(filtered, filterParams);

    // Apply favorites filter
    if (filterParams.favorites) {
        filtered = filtered.filter(isParticipantFavorite);
    }
    if (filterParams.mutualFavorites) {
        filtered = filtered.filter(isParticipantMutualFavorite);
    }

    // Use queryBuilder structure
    if (filterParams.query) {
        let filter = createMatchObjectFilter(filterParams.query);
        filtered = filtered.filter(filter);
    }

    return {filteredParticipants: filtered, categories, filterParams};
}

const indexConnections = memoizee((connections = []) =>
    connections.reduce((index, connection) => {
        index[connection.id] = connection;
        return index;
    }, [])
);
